import React from "react";
import {
  Box,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import { MdCancel, MdCheckCircle, MdMoreVert } from "react-icons/md";
import { IoMdAddCircle } from "react-icons/io";
import { FaEdit, FaEye } from "react-icons/fa";
import Swal from "sweetalert2";

const NeedsByServiceTypeTableRow = ({
  row,
  title,
  clickEventAdd,
  clickEventView,
  clickEventEdit,
  stateOpen,
}) => {
  const [open, setOpen] = React.useState(stateOpen);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedItem, setSelectedItem] = React.useState(null);

  React.useEffect(() => {
    setOpen(stateOpen);
  }, [stateOpen]);

  const handleMenuOpen = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleMenuAction = (action) => {
    if (selectedItem) {
      switch (action) {
        case 'add':
          clickEventAdd(selectedItem);
          break;
        case 'view':
          clickEventView(selectedItem);
          break;
        case 'edit':
          clickEventEdit(selectedItem);
          break;
        default:
          break;
      }
    }
    handleMenuClose();
  };

  const handleInvalidData = (row) => {
    const invalidItem = row.attributes.some((item) => {
      const hasNaNValue = [
        item.expectedProvidersNeedsCity,
        item.availableProvidersCity,
        item.availableProvidersCityWithoutAdjustment,
        item.networkAjustmentFactor,
      ].some(isNaN);

      if (hasNaNValue) {
        console.error(
          "Erro: Valores NaN encontrados no relatório durante a construção da tabela:",
          item
        );
      }

      return hasNaNValue;
    });

    if (invalidItem) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Ocorreu um erro ao carregar os dados do relatório. Tente novamente e, se o problema persistir, entre em contato com um administrador.",
        confirmButtonText: "OK",
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.reload();
        }
      });
    }
  };

  const renderActionButtons = (item) => {
    const available = item.availableProvidersCity ?? item.availableProvidersCityWithoutAdjustment;
    const expected = item.expectedProvidersNeedsCity ?? 0;
    const difference = available - expected;
    const roundedDifference = Math.floor(difference);

    if (roundedDifference === 0) {
      return (
        <div className="flex justify-center">
          <span className="text-xs text-gray-500">Sem ações necessárias</span>
        </div>
      );
    }

    return (
      <div className="flex flex-row gap-2 justify-start items-center">
        {item.isCauseAndActionCreated ? (
          <MdCheckCircle className="text-lg text-green-600" title='Esse atributo já possui causa raiz e plano de ação.' />
        ) : (
          <MdCancel className="text-lg text-red-600" title='Sem causa raiz e plano de ação para esse atributo.' />
        )}
        <IconButton 
          size="small"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleMenuOpen(event, item);
          }}
          sx={{ 
            padding: '4px',
            color: '#6b7280',
            '&:hover': { 
              backgroundColor: '#f3f4f6',
              color: '#374151'
            } 
          }}
        >
          <MdMoreVert fontSize="small" />
        </IconButton>
      </div>
    );
  };

  const renderDataCell = (value, showBackground = true) => {
    const baseClasses =
      "inline-flex items-center px-2 py-1 rounded text-xs font-medium";
    const bgClasses = showBackground
      ? "bg-gray-100 text-gray-700 border border-gray-200"
      : "text-gray-700";

    return <span className={`${baseClasses} ${bgClasses}`}>{value}</span>;
  };

  const renderSufficiencyCell = (item) => {
    const available = item.availableProvidersCity || item.availableProvidersCityWithoutAdjustment;
    const expected = item.expectedProvidersNeedsCity;
    const difference = available - expected;
    const roundedDifference = Math.floor(difference);

    let colorClasses;
    if (roundedDifference < 0) {
      colorClasses = "bg-red-50 text-red-700 border-red-200";
    } else if (roundedDifference === 0) {
      colorClasses = "bg-gray-100 text-gray-700 border-gray-200";
    } else {
      colorClasses = "bg-yellow-50 text-yellow-700 border-yellow-200";
    }

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${colorClasses}`}
      >
        {roundedDifference}
      </span>
    );
  };

  const renderSufficiencyStatusCell = (item) => {
    const available = item.availableProvidersCity || item.availableProvidersCityWithoutAdjustment;
    const expected = item.expectedProvidersNeedsCity;
    const difference = available - expected;
    
    const roundedDifference = Math.floor(difference);

    let text, colorClasses;
    
    if (roundedDifference === 0) {
      text = "Rede Suficiente";
      colorClasses = "bg-blue-50 text-blue-700 border-blue-200";
    } else if (roundedDifference < 0) {
      text = "Rede Insuficiente";
      colorClasses = "bg-red-50 text-red-700 border-red-200";
    } else {
      text = "Rede Excedente";
      colorClasses = "bg-yellow-50 text-yellow-700 border-yellow-200";
    }

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${colorClasses}`}
      >
        {text}
      </span>
    );
  };

  const renderTableRow = (item, index = 0) => {
    const isHighlighted =
      item.serviceType === "SADT" ||
      (item.serviceType && item.serviceType.toLowerCase().includes("serviço"));
    const available = item.availableProvidersCity ?? item.availableProvidersCityWithoutAdjustment;
    const expected = item.expectedProvidersNeedsCity ?? 0;
    const difference = available - expected;
    const roundedDifference = Math.floor(difference);
    const hasActions = roundedDifference !== 0;

    return (
      <TableRow
        key={index}
        onClick={hasActions ? (event) => {
          if (!event.target.closest('button') && !event.target.closest('[role="button"]') && !event.target.closest('.MuiIconButton-root')) {
            handleMenuOpen(event, item);
          }
        } : undefined}
        sx={{
          backgroundColor: isHighlighted 
            ? "#f5f5f5" 
            : index % 2 === 0 
            ? "#ffffff" 
            : "#fafafa",
          borderBottom: "1px solid #f0f0f0",
          "&:hover": { 
            backgroundColor: hasActions ? "#f0f9ff" : "#f5f5f5",
            cursor: hasActions ? "pointer" : "default"
          },
        }}
      >
        <TableCell sx={{ fontSize: {xs: "0.75rem", sm: "0.815rem"}, color: "#4b5563", py: {xs: 0.75, sm: 1} }}>
          {item.referenceCode}
        </TableCell>
        <TableCell sx={{ fontSize: {xs: "0.75rem", sm: "0.815rem"}, color: "#4b5563", py: {xs: 0.75, sm: 1} }}>
          {item.serviceType || "Prestador Desconhecido"}
        </TableCell>
        <TableCell sx={{ fontSize: {xs: "0.75rem", sm: "0.815rem"}, color: "#4b5563", py: {xs: 0.75, sm: 1} }}>
          {item.parameterName}
        </TableCell>
        <TableCell sx={{ fontSize: {xs: "0.75rem", sm: "0.815rem"}, color: "#4b5563", py: {xs: 0.75, sm: 1} }}>
          {renderDataCell(Math.ceil(item.expectedProvidersNeedsCity))}
        </TableCell>
        <TableCell sx={{ fontSize: {xs: "0.75rem", sm: "0.815rem"}, color: "#4b5563", py: {xs: 0.75, sm: 1} }}>
          {renderDataCell(
            item.availableProvidersCityWithoutAdjustment ||
              item.availableProvidersCity,
            false
          )}
        </TableCell>
        <TableCell sx={{ fontSize: {xs: "0.75rem", sm: "0.815rem"}, color: "#4b5563", py: {xs: 0.75, sm: 1} }}>
          {renderDataCell(
            `${
              (item.availableProvidersCityWithoutAdjustment ||
                item.availableProvidersCity) === 0
                ? "0"
                : item.networkAjustmentFactor
            }%`,
            false
          )}
        </TableCell>
        <TableCell sx={{ fontSize: {xs: "0.75rem", sm: "0.815rem"}, color: "#4b5563", py: 1 }}>
          {renderDataCell(
            item.availableProvidersCity ||
              item.availableProvidersCityWithoutAdjustment
          )}
        </TableCell>
        <TableCell sx={{ fontSize: {xs: "0.75rem", sm: "0.815rem"}, py: 1 }}>
          {renderSufficiencyCell(item)}
        </TableCell>
        <TableCell sx={{ fontSize: {xs: "0.75rem", sm: "0.815rem"}, py: 1 }}>
          {renderSufficiencyStatusCell(item)}
        </TableCell>
        <TableCell sx={{ fontSize: {xs: "0.75rem", sm: "0.815rem"}, color: "#4b5563", py: 1 }}>
          {renderActionButtons(item)}
        </TableCell>
      </TableRow>
    );
  };

  handleInvalidData(row);

  // Se o grupo só tem um item, mostra diretamente como linha de parâmetro
  const showDirectly = row.attributes.length === 1;

  if (showDirectly) {
    return (
      <>
        {renderTableRow(row.attributes[0])}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          disablePortal={false}
          disableAutoFocus={true}
          disableEnforceFocus={true}
          keepMounted={false}
          container={() => document.body}
          slotProps={{
            root: {
              slotProps: {
                backdrop: {
                  invisible: true
                }
              }
            }
          }}
          PaperProps={{
            sx: {
              minWidth: 200,
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
              border: '1px solid #e5e7eb',
              zIndex: 1300
            }
          }}
        >
          <MenuItem 
            onClick={() => handleMenuAction('add')}
            sx={{ 
              py: 1.5,
              fontSize: '0.875rem',
              '&:hover': { backgroundColor: '#f3f4f6' }
            }}
          >
            <IoMdAddCircle className="mr-3 text-lg text-blue-600" />
            Adicionar Causa Raiz/Plano de Ação
          </MenuItem>
          
          {selectedItem?.isCauseAndActionCreated && (
            <>
              <MenuItem 
                onClick={() => handleMenuAction('view')}
                sx={{ 
                  py: 1.5,
                  fontSize: '0.875rem',
                  '&:hover': { backgroundColor: '#f3f4f6' }
                }}
              >
                <FaEye className="mr-3 text-sm text-green-600" />
                Visualizar Causa Raiz/Plano de Ação
              </MenuItem>
              <MenuItem 
                onClick={() => handleMenuAction('edit')}
                sx={{ 
                  py: 1.5,
                  fontSize: '0.875rem',
                  '&:hover': { backgroundColor: '#f3f4f6' }
                }}
              >
                <FaEdit className="mr-3 text-sm text-orange-600" />
                Editar Causa Raiz/Plano de Ação
              </MenuItem>
            </>
          )}
        </Menu>
      </>
    );
  }

  return (
    <>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 0,
                padding: 1,
                borderTop: "1px solid #f0f0f0",
                backgroundColor: "#fafafa",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#fdfdfd",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                      }}
                    >
                      Código
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                      }}
                    >
                      Tipo Prestador
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                      }}
                    >
                      {title}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                      }}
                    >
                      Necessidade Serviço
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                      }}
                    >
                      Nº Serviço s/ Fator
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                      }}
                    >
                      Fator de Ajuste
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                      }}
                    >
                      Nº Serviço + Fator
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                      }}
                    >
                      Diferença
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                      }}
                    >
                      Suficiência de Rede
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#374151",
                        py: 1.5,
                      }}
                    >
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.attributes.map((item, index) =>
                    renderTableRow(item, index)
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        disablePortal={false}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        keepMounted={false}
        container={() => document.body}
        slotProps={{
          root: {
            slotProps: {
              backdrop: {
                invisible: true
              }
            }
          }
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            border: '1px solid #e5e7eb',
            zIndex: 1300
          }
        }}
      >
        <MenuItem 
          onClick={() => handleMenuAction('add')}
          sx={{ 
            py: 1.5,
            fontSize: '0.875rem',
            '&:hover': { backgroundColor: '#f3f4f6' }
          }}
        >
          <IoMdAddCircle className="mr-3 text-lg text-blue-600" />
          Adicionar Causa Raiz/Plano de Ação
        </MenuItem>
        
        {selectedItem?.isCauseAndActionCreated && (
          <>
            <MenuItem 
              onClick={() => handleMenuAction('view')}
              sx={{ 
                py: 1.5,
                fontSize: '0.875rem',
                '&:hover': { backgroundColor: '#f3f4f6' }
              }}
            >
              <FaEye className="mr-3 text-sm text-green-600" />
              Visualizar Causa Raiz/Plano de Ação
            </MenuItem>
            <MenuItem 
              onClick={() => handleMenuAction('edit')}
              sx={{ 
                py: 1.5,
                fontSize: '0.875rem',
                '&:hover': { backgroundColor: '#f3f4f6' }
              }}
            >
              <FaEdit className="mr-3 text-sm text-orange-600" />
              Editar Causa Raiz/Plano de Ação
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default NeedsByServiceTypeTableRow;